import { useState, useContext, useEffect } from "react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, googleProvider, microsoftProvider, firestore } from "firebaseConfig";
import { createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { doc, setDoc, collection, addDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { updateProfile } from "firebase/auth";
import { getDoc } from "firebase/firestore";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton"; // Importado para o botão "X"
import CloseIcon from "@mui/icons-material/Close"; // Ícone "X"

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Images
import logoBranca from "assets/images/logos/LogoSemFundo.png";

function SignUp() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false); // Estado para o checkbox de termos

  const handleOpenModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = () => {
    setAgreeTerms(!agreeTerms);
  };

  const translateFirebaseError = (errorCode) => {
    switch (errorCode) {
      case "auth/weak-password":
        return "A senha deve ter pelo menos 6 caracteres.";
      case "auth/email-already-in-use":
        return "Este e-mail já está em uso.";
      case "auth/invalid-email":
        return "Por favor, insira um e-mail válido.";
      default:
        return "Ocorreu um erro. Tente novamente.";
    }
  };

  const createUserDocument = async (uid, name, email) => {
    try {
      const userRef = doc(firestore, "usuarios", uid);

      // Fim do teste em 7 dias
      const freeUntilDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

      await setDoc(
        userRef,
        {
          // básicos
          createdAt: serverTimestamp(), // pode ficar como timestamp; os antigos continuam string
          email,
          name,
          role: "cliente",

          // trial do app (7 dias liberados)
          freeUntil: Timestamp.fromDate(freeUntilDate),
          isBlocked: false, // liberado durante o trial

          // placeholders de assinatura (Stripe) – começam vazios
          plan: null,
          subscriptionStatus: null,
          stripeCustomerId: null,
          stripeSubscriptionId: null,
          cancelAtPeriodEnd: false,

          // ajuda o front a mostrar a data no cartão de assinatura
          currentPeriodEnd: Timestamp.fromDate(freeUntilDate),

          // opcional: sinaliza para mostrar um pop-up no primeiro login
          showTrialPopup: true,
        },
        { merge: true }
      );

      // coleções/categorias padrão
      const categoriasPadrao = [
        { nome: "Salário", emoji: "💰", tipo: "receita" },
        { nome: "Freelance", emoji: "🧑‍💻", tipo: "receita" },
        { nome: "Rendimento de Investimentos", emoji: "📈", tipo: "receita" },
        { nome: "Bônus", emoji: "🎉", tipo: "receita" },
        { nome: "Aluguel", emoji: "🏠", tipo: "despesaFixa" },
        { nome: "Internet", emoji: "🌐", tipo: "despesaFixa" },
        { nome: "Energia Elétrica", emoji: "💡", tipo: "despesaFixa" },
        { nome: "Plano de Saúde", emoji: "🏥", tipo: "despesaFixa" },
        { nome: "Alimentação", emoji: "🍽️", tipo: "despesaVariavel" },
        { nome: "Transporte", emoji: "🚗", tipo: "despesaVariavel" },
        { nome: "Lazer", emoji: "🎬", tipo: "despesaVariavel" },
        { nome: "Restaurantes", emoji: "🍴", tipo: "despesaVariavel" },
      ];
      const categoriasRef = collection(firestore, "usuarios", uid, "categorias");
      for (const cat of categoriasPadrao) await addDoc(categoriasRef, cat);

      // documentos “template”
      await setDoc(doc(firestore, "usuarios", uid, "lancamentos", "template"), {});
      await setDoc(doc(firestore, "usuarios", uid, "investimentos", "template"), {});
    } catch (error) {
      console.error("Erro ao criar documento do usuário e/ou subcoleções:", error);
      throw new Error("Erro ao configurar o usuário no Firestore.");
    }
  };

  const handleSignUp = async () => {
    if (!agreeTerms) {
      handleOpenModal("Você precisa concordar com os Termos e Condições.");
      return;
    }

    if (!formData.email.includes("@") || formData.email.length < 5) {
      handleOpenModal("Por favor, insira um e-mail válido.");
      return;
    }

    try {
      console.log("Iniciando processo de cadastro...");
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // Atualiza o displayName do usuário no Firebase Authentication
      await updateProfile(userCredential.user, {
        displayName: formData.name,
      });
      console.log("Nome do usuário atualizado no Firebase.");

      // Cria o documento principal no Firestore com a role "cliente"
      const userId = userCredential.user.uid;
      await setDoc(doc(firestore, "usuarios", userId), {
        name: formData.name,
        email: formData.email,
        role: "cliente",
        createdAt: new Date().toISOString(),
      });
      console.log("Documento principal do usuário criado com sucesso.");

      // ➜ TRIAL 7 DIAS (patch mínimo, sem alterar o restante)
      try {
        const freeUntilDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        await setDoc(
          doc(firestore, "usuarios", userId),
          {
            freeUntil: Timestamp.fromDate(freeUntilDate),
            currentPeriodEnd: Timestamp.fromDate(freeUntilDate),
            isBlocked: false,
            // placeholders Stripe
            plan: null,
            subscriptionStatus: null,
            stripeCustomerId: null,
            stripeSubscriptionId: null,
            cancelAtPeriodEnd: false,
            showTrialPopup: true,
          },
          { merge: true }
        );
      } catch (e) {
        console.error("Falha ao definir trial de 7 dias:", e);
      }

      // Exibe mensagem de sucesso imediatamente
      handleOpenModal("Conta criada com sucesso! Faça login para acessar o sistema.");

      // Inicia as operações restantes em segundo plano (não bloqueia o usuário)
      (async () => {
        try {
          // Configura categorias padrão
          const categoriasPadrao = [
            { nome: "Salário", emoji: "💰", tipo: "receita" },
            { nome: "Freelance", emoji: "🧑‍💻", tipo: "receita" },
            { nome: "Rendimento de Investimentos", emoji: "📈", tipo: "receita" },
            { nome: "Bônus", emoji: "🎉", tipo: "receita" },
            { nome: "Aluguel", emoji: "🏠", tipo: "despesaFixa" },
            { nome: "Internet", emoji: "🌐", tipo: "despesaFixa" },
            { nome: "Energia Elétrica", emoji: "💡", tipo: "despesaFixa" },
            { nome: "Plano de Saúde", emoji: "🏥", tipo: "despesaFixa" },
            { nome: "Alimentação", emoji: "🍽️", tipo: "despesaVariavel" },
            { nome: "Transporte", emoji: "🚗", tipo: "despesaVariavel" },
            { nome: "Lazer", emoji: "🎬", tipo: "despesaVariavel" },
            { nome: "Restaurantes", emoji: "🍴", tipo: "despesaVariavel" },
          ];

          const categoriasCollectionRef = collection(firestore, "usuarios", userId, "categorias");
          for (const categoria of categoriasPadrao) {
            await addDoc(categoriasCollectionRef, categoria);
          }
          console.log("Categorias padrão inseridas com sucesso.");

          // Cria documentos vazios nas coleções
          await setDoc(doc(firestore, "usuarios", userId, "lancamentos", "template"), {});
          await setDoc(doc(firestore, "usuarios", userId, "investimentos", "template"), {});
          console.log("Coleções padrão configuradas com sucesso.");
        } catch (error) {
          console.error("Erro ao configurar categorias e coleções padrão:", error);
        }

        // Logout após concluir todas as operações
        try {
          await auth.signOut();
          console.log("Usuário deslogado após criação da conta.");
        } catch (logoutError) {
          console.error("Erro ao deslogar usuário:", logoutError);
        }
      })();

      // Redireciona para a tela de login após exibir o modal
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 2000);
    } catch (error) {
      console.error("Erro ao criar a conta:", error);
      const translatedMessage = translateFirebaseError(error.code);
      handleOpenModal(`Erro: ${translatedMessage}`);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!agreeTerms) {
      handleOpenModal("Você precisa concordar com os Termos e Condições.");
      return;
    }

    try {
      console.log("Iniciando cadastro com Google...");
      const userCredential = await signInWithPopup(auth, googleProvider);

      // Obter as informações do usuário
      const user = userCredential.user;
      const userRef = doc(firestore, "usuarios", user.uid);
      const existingUserDoc = await getDoc(userRef);

      // Verifica se o usuário já existe no Firestore
      if (existingUserDoc.exists()) {
        console.log("Usuário já existe no Firestore.");
        handleOpenModal("Já existe uma conta vinculada a este e-mail. Faça login para acessar o sistema.");
        await auth.signOut(); // Desloga imediatamente
        return;
      }

      // Se não existir, cria o documento no Firestore (com trial via createUserDocument)
      const displayName = user.displayName || "Usuário Google";
      await createUserDocument(user.uid, displayName, user.email);

      console.log("Usuário criado com sucesso com Google.");
      handleOpenModal("Conta criada com sucesso com o Google! Faça login para acessar o sistema.");
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 2000);
    } catch (error) {
      console.error("Erro ao criar conta com Google:", error);
      handleOpenModal(`Erro: ${translateFirebaseError(error.code)}`);
    }
  };

  const handleMicrosoftSignUp = async () => {
    if (!agreeTerms) {
      handleOpenModal("Você precisa concordar com os Termos e Condições.");
      return;
    }

    try {
      console.log("Iniciando cadastro com Microsoft...");
      const userCredential = await signInWithPopup(auth, microsoftProvider);
      console.log("Credenciais obtidas:", userCredential);

      // Verifica se o e-mail já está em uso
      const existingUserRef = doc(firestore, "usuarios", userCredential.user.uid);
      const existingUserDoc = await getDoc(existingUserRef);

      if (existingUserDoc.exists()) {
        console.log("E-mail já em uso.");
        handleOpenModal("Este e-mail já está em uso. Tente redefinir sua senha.");
        await auth.signOut();
        return;
      }

      // Criação de documento (com trial via createUserDocument)
      const displayName = userCredential.user.displayName || "Usuário Microsoft";
      await createUserDocument(userCredential.user.uid, displayName, userCredential.user.email);

      console.log("Usuário criado com sucesso com Microsoft.");
      handleOpenModal("Conta criada com sucesso com o Microsoft! Faça login para acessar o sistema.");
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 2000);
    } catch (error) {
      console.error("Erro ao criar conta com Microsoft:", error.message);
      handleOpenModal(`Erro: ${error.message}`);
    }
  };

  // Adiciona CSS Global para remover zoom e tela fixa
  useEffect(() => {
    let lastTouchDistance = 0;
    const content = document.getElementById("content-wrapper");

    const handleTouchStart = (event) => {
      if (event.touches.length === 2) {
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        lastTouchDistance = Math.hypot(touch2.pageX - touch1.pageX, touch2.pageY - touch1.pageY);
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(touch2.pageX - touch1.pageX, touch2.pageY - touch1.pageY);

        const scale = Math.min(1.5, Math.max(1, currentDistance / lastTouchDistance));
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = "center";
      }
    };

    const handleTouchEnd = () => {
      content.style.transition = "transform 0.3s ease";
      content.style.transform = "scale(1)";
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  return (
    <MDBox
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        backgroundColor: "#F7F9FC",
      }}
    >
      {/* Seção Esquerda - Decorativa (com Logomarca) */}
      <MDBox
        sx={{
          display: { xs: "none", md: "flex" },
          width: { md: "50%" },
          background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
          alignItems: "flex-start",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Conteúdo */}
        <MDBox sx={{ position: "relative", zIndex: 10, maxWidth: "500px", pt: 4, px: 6, mt: 4, color: "white" }}>
          {/* Logomarca */}
          <MDBox textAlign="center" mb={2}>
            <Link to="/">
              <img
                src={logoBranca}
                alt="Logomarca"
                width="200"
                height="200"
                style={{ transition: "transform 0.3s ease", "&:hover": { transform: "scale(1.05)" } }}
              />
            </Link>
          </MDBox>

          <MDTypography
            variant="h2"
            fontWeight="bold"
            mb={4}
            sx={{
              fontFamily: "'Roboto Slab', serif",
              letterSpacing: "1px",
              background: "linear-gradient(90deg, #FFFFFF 0%, #E2E8F0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            A Liberdade Financeira Começa Aqui
          </MDTypography>
          <MDTypography
            variant="body1"
            mb={6}
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "1.1rem",
              lineHeight: "1.6",
            }}
          >
            Assuma o controle das suas finanças com nossas ferramentas abrangentes e insights personalizados para o seu
            sucesso financeiro.
          </MDTypography>

          {/* Lista de Recursos */}
          <MDBox component="ul">
            {[
              "Painel Personalizado",
              "Gestão Inteligente de Orçamento",
              "Acompanhamento de Metas Financeiras",
              "Relatórios Financeiros Detalhados",
            ].map((feature, index) => (
              <MDBox key={index} component="li" display="flex" alignItems="center" mb={3}>
                <MDBox
                  sx={{
                    width: "10px",
                    height: "10px",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    borderRadius: "50%",
                    mr: 3,
                  }}
                />
                <MDTypography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "1rem",
                  }}
                >
                  {feature}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Seção Direita - Formulário de Cadastro */}
      <MDBox
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 4, md: 8 },
        }}
      >
        <MDBox sx={{ width: "100%", maxWidth: "450px" }}>
          {/* Título e Subtítulo */}
          <MDBox textAlign="center" mb={8}>
            <MDTypography
              variant="h3"
              fontWeight="bold"
              color="dark"
              mb={2}
              sx={{
                fontFamily: "'Roboto Slab', serif",
                letterSpacing: "0.5px",
                background: "linear-gradient(90deg, #1E3A8A 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Crie sua Conta
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem", opacity: 0.8 }}>
              Preencha os dados para começar
            </MDTypography>
          </MDBox>

          {/* Formulário */}
          <Card
            sx={{
              p: 6,
              borderRadius: "20px",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <MDBox
              component="form"
              role="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSignUp();
              }}
            >
              <MDBox mb={4}>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  mb={1}
                  display="block"
                  sx={{ color: "#1E3A8A", fontSize: "0.9rem" }}
                >
                  Nome
                </MDTypography>
                <MDInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu Nome"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      height: "48px",
                      border: "1px solid #E2E8F0",
                      transition: "all 0.3s ease",
                      "&:hover": { borderColor: "#6D28D9" },
                    },
                    "& input": { color: "#1E3A8A", fontSize: "1rem" },
                  }}
                />
              </MDBox>

              <MDBox mb={4}>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  mb={1}
                  display="block"
                  sx={{ color: "#1E3A8A", fontSize: "0.9rem" }}
                >
                  Email
                </MDTypography>
                <MDInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      height: "48px",
                      border: "1px solid #E2E8F0",
                      transition: "all 0.3s ease",
                      "&:hover": { borderColor: "#6D28D9" },
                    },
                    "& input": { color: "#1E3A8A", fontSize: "1rem" },
                  }}
                />
              </MDBox>

              <MDBox mb={4}>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  mb={1}
                  display="block"
                  sx={{ color: "#1E3A8A", fontSize: "0.9rem" }}
                >
                  Senha
                </MDTypography>
                <MDInput
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      height: "48px",
                      border: "1px solid #E2E8F0",
                      transition: "all 0.3s ease",
                      "&:hover": { borderColor: "#6D28D9" },
                    },
                    "& input": { color: "#1E3A8A", fontSize: "1rem" },
                  }}
                />
              </MDBox>

              <MDBox display="flex" alignItems="center" mb={4}>
                <Checkbox checked={agreeTerms} onChange={handleCheckboxChange} />
                <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", ml: 1 }}>
                  Eu concordo com os{" "}
                  <MDTypography
                    component="a"
                    href="#"
                    variant="body2"
                    sx={{
                      color: "#6D28D9",
                      fontWeight: "medium",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Termos e Condições
                  </MDTypography>
                </MDTypography>
              </MDBox>

              <MDButton
                type="submit"
                fullWidth
                sx={{
                  background: "linear-gradient(90deg, #3B82F6 0%, #8B5CF6 100%)",
                  color: "#FFFFFF",
                  borderRadius: "12px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: "medium",
                  fontSize: "1rem",
                  boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)",
                    boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    color: "#FFFFFF",
                    opacity: 0.6,
                  },
                }}
              >
                Cadastrar
              </MDButton>

              <MDBox mt={6}>
                <MDBox position="relative">
                  <MDBox sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
                    <MDBox sx={{ width: "100%", borderTop: "1px solid", borderColor: "grey.200" }} />
                  </MDBox>
                  <MDBox sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                    <MDTypography
                      variant="body2"
                      sx={{ px: 3, bgcolor: "rgba(255, 255, 255, 0.95)", color: "grey.500", fontSize: "0.9rem" }}
                    >
                      OU
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox mt={6}>
                  <MDButton
                    variant="outlined"
                    fullWidth
                    onClick={handleGoogleSignUp}
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      borderColor: "grey.300",
                      color: "#1E3A8A",
                      textTransform: "none",
                      fontWeight: "medium",
                      fontSize: "1rem",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "grey.50",
                        borderColor: "grey.400",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <MDBox
                      component="img"
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google Icon"
                      sx={{ width: "24px", height: "24px", mr: 2 }}
                    />
                    Criar conta com Google
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox textAlign="center" mt={6}>
                <MDTypography variant="body2" sx={{ color: "grey.600", fontSize: "0.9rem" }}>
                  Já tem uma conta?{" "}
                  <Link
                    to="/authentication/sign-in"
                    style={{
                      color: "#6D28D9",
                      fontWeight: "medium",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Entrar
                  </Link>
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      {/* Modal de Alerta */}
      <Modal open={showModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
            p: 4,
            textAlign: "center",
            position: "relative", // Para posicionar o botão "X"
          }}
        >
          {/* Botão "X" no canto superior direito */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#1E3A8A",
              "&:hover": {
                color: "#6D28D9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <MDTypography variant="h6" sx={{ color: "#1E3A8A" }}>
            {modalMessage}
          </MDTypography>
        </Box>
      </Modal>
    </MDBox>
  );
}

export default SignUp;
